import { Button, Box, Typography, Grid, Paper, CircularProgress, Chip, Alert } from "@mui/material";
import { useState, useEffect } from "react";
import { useServiceRequestQueries, Bid, formatDate } from "shared-lib";
import { useVendorContext } from "../../context/VendorContextProvider";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { getFirestore, doc, getDoc} from "firebase/firestore";
import { useNavigate } from "react-router-dom";

interface CompletedStepProps {
  accountData: any;
  invoiceCreating: boolean;
  invoiceCreated: boolean;
  onCreateInvoice: (email: string, amount: string, description: string, bidId?: string) => Promise<void>;
}

// Simplify the ServiceRequest type to avoid type errors
interface SimpleServiceRequest {
  id: string;
  serviceName?: string;
  location?: string;
  createdAt?: Date;
  updatedAt?: Date;
  biddingEndDate?: Date;
  clientId?: string;
  [key: string]: any; // Allow any other properties
}

// Add client address information
interface ClientInfo {
  contactEmail: string;
  companyName?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

interface BidWithServiceRequest extends Bid {
  serviceRequest?: SimpleServiceRequest;
  clientInfo?: ClientInfo;
}

const CompletedStep = ({ 
  invoiceCreating, 
  invoiceCreated, 
  onCreateInvoice 
}: CompletedStepProps) => {
  const { currentVendor } = useVendorContext();
  const { useVendorBids } = useServiceRequestQueries();
  const [winningBids, setWinningBids] = useState<BidWithServiceRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch all bids from this vendor
  const { data: vendorBids = [], isLoading: bidsLoading } = useVendorBids(currentVendor?.id || "");

  // Get service request details for each winning bid
  useEffect(() => {
    const fetchServiceRequestDetails = async () => {
      if (bidsLoading || !vendorBids.length) return;

      try {
        setLoading(true);
        
        // Filter for accepted bids
        const acceptedBids = vendorBids.filter(bid => 
          bid.intent === 'bid' && bid.status === 'accepted'
        );
        
        const bidsWithDetails: BidWithServiceRequest[] = [];
        
        // Get service request details for each bid
        for (const bid of acceptedBids) {
          try {
            const db = getFirestore();
            
            // Get service request
            const requestRef = doc(db, 'service_requests', bid.requestId);
            const requestDoc = await getDoc(requestRef);
            
            if (requestDoc.exists()) {
              const requestData = requestDoc.data();
              
              // Get client info for invoice
              let clientInfo: ClientInfo = {
                contactEmail: ''
              };
              
              if (requestData.clientId) {
                const clientRef = doc(db, 'clients', requestData.clientId);
                const clientDoc = await getDoc(clientRef);
                if (clientDoc.exists()) {
                  const clientData = clientDoc.data();
                  clientInfo = {
                    contactEmail: clientData.contactEmail || '',
                    companyName: clientData.companyName || '',
                    address: clientData.address || '',
                    city: clientData.city || '',
                    state: clientData.state || '',
                    zipCode: clientData.zipCode || ''
                  };
                }
              }
              
              bidsWithDetails.push({
                ...bid,
                serviceRequest: {
                  id: requestDoc.id,
                  ...requestData,
                  createdAt: requestData.createdAt?.toDate(),
                  updatedAt: requestData.updatedAt?.toDate(),
                  biddingEndDate: requestData.biddingEndDate?.toDate(),
                } as SimpleServiceRequest,
                clientInfo,
                invoiceSent: bid.invoiceSent || false
              });
            }
          } catch (error) {
            console.error("Error fetching request details:", error);
          }
        }
        
        setWinningBids(bidsWithDetails);
      } catch (error) {
        console.error("Error fetching winning bids:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchServiceRequestDetails();
  }, [vendorBids, bidsLoading, currentVendor]);

  const handleSendInvoice = async (bid: BidWithServiceRequest) => {
    if (!bid.clientInfo?.contactEmail) {
      console.error("No client email available");
      return;
    }
    
    const amount = bid.amount?.toString() || "0";
    
    // Create a more detailed description including address
    let description = `Invoice for ${bid.serviceRequest?.serviceName || 'Service'}`;
    
    // Add service address if available
    if (bid.serviceRequest?.location) {
      description += ` - ${bid.serviceRequest.location}`;
    } else if (bid.clientInfo.address) {
      // Use client address if service location not specified
      const addressParts = [];
      if (bid.clientInfo.address) addressParts.push(bid.clientInfo.address);
      if (bid.clientInfo.city) addressParts.push(bid.clientInfo.city);
      if (bid.clientInfo.state) addressParts.push(bid.clientInfo.state);
      if (bid.clientInfo.zipCode) addressParts.push(bid.clientInfo.zipCode);
      
      if (addressParts.length > 0) {
        description += ` - ${addressParts.join(', ')}`;
      }
    }
    
    // Add property/company name if available
    if (bid.clientInfo.companyName) {
      description += ` (${bid.clientInfo.companyName})`;
    }
    
    try {
      // Send invoice
      await onCreateInvoice(bid.clientInfo.contactEmail, amount, description, bid.id);
      
      // Update local state only - Firestore update is done in the cloud function
      setWinningBids(prevBids => 
        prevBids.map(b => 
          b.id === bid.id ? { ...b, invoiceSent: true } : b
        )
      );
    } catch (error) {
      console.error("Error sending invoice:", error);
    }
  };

  const goToServiceRequest = (requestId: string) => {
    navigate(`/requests/${requestId}`);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, width: "100%" }}>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Your Stripe integration is complete! You can now receive payments through our platform.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>    
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Winning Bids
            </Typography>
            
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
                <CircularProgress />
              </Box>
            ) : winningBids.length > 0 ? (
              <Grid container spacing={2}>
                {winningBids.map((bid) => (
                  <Grid item xs={12} key={bid.id}>
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 2.5, 
                        mb: 1.5, 
                        border: "1px solid rgba(0, 171, 155, 0.3)",
                        borderLeft: "5px solid #00AB9B",
                        borderRadius: "16px",
                        bgcolor: "rgba(0, 171, 155, 0.05)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: "0px 8px 32px -4px rgba(16, 29, 59, 0.1)"
                        }
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={8}>
                          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                            <CheckCircleIcon sx={{ color: "#00AB9B", mr: 1.5, fontSize: "1.25rem" }} />
                            <Typography variant="h6" sx={{ 
                              fontWeight: 600,
                              color: "#101D3B",
                              fontSize: "18px"
                            }}>
                              {bid.serviceRequest?.serviceName || "Service Request"}
                            </Typography>
                            
                            <Chip 
                              size="small"
                              label="ACCEPTED"
                              sx={{
                                ml: 2,
                                bgcolor: "rgba(0, 171, 155, 0.1)",
                                color: "#00AB9B",
                                fontWeight: 600,
                                fontSize: "11px",
                                height: "22px",
                                borderRadius: "11px"
                              }}
                            />
                          </Box>
                          
                          <Grid container spacing={2} sx={{ mb: 1 }}>
                            <Grid item xs={12} md={6}>
                              {/* Location info */}
                              {bid.serviceRequest?.location ? (
                                <Box sx={{ display: "flex", mb: 1, alignItems: "flex-start" }}>
                                  <Typography variant="body2" sx={{ color: "rgba(16, 29, 59, 0.6)", width: "90px", textAlign: "left" }}>
                                    Location:
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: "#101D3B", fontWeight: 500, textAlign: "left" }}>
                                    {bid.serviceRequest.location}
                                  </Typography>
                                </Box>
                              ) : (
                                <>
                                  {bid.clientInfo?.address && (
                                    <Box sx={{ display: "flex", mb: 1, alignItems: "flex-start" }}>
                                      <Typography variant="body2" sx={{ color: "rgba(16, 29, 59, 0.6)", width: "75px", textAlign: "left" }}>
                                        Address:
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: "#101D3B", fontWeight: 500, textAlign: "left" }}>
                                        {bid.clientInfo.address}
                                      </Typography>
                                    </Box>
                                  )}
                                  {(bid.clientInfo?.city || bid.clientInfo?.state || bid.clientInfo?.zipCode) && (
                                    <Box sx={{ display: "flex", mb: 1, alignItems: "flex-start" }}>
                                      <Typography variant="body2" sx={{ color: "rgba(16, 29, 59, 0.6)", width: "75px", textAlign: "left" }}>
                                        City/State:
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: "#101D3B", fontWeight: 500, textAlign: "left" }}>
                                        {[
                                          bid.clientInfo?.city,
                                          bid.clientInfo?.state,
                                          bid.clientInfo?.zipCode
                                        ].filter(Boolean).join(', ')}
                                      </Typography>
                                    </Box>
                                  )}
                                </>
                              )}
                              
                              {bid.clientInfo?.companyName && (
                                <Box sx={{ display: "flex", mb: 1, alignItems: "flex-start" }}>
                                  <Typography variant="body2" sx={{ color: "rgba(16, 29, 59, 0.6)", width: "75px", textAlign: "left" }}>
                                    Property:
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: "#101D3B", fontWeight: 500, textAlign: "left" }}>
                                    {bid.clientInfo.companyName}
                                  </Typography>
                                </Box>
                              )}
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Box sx={{ display: "flex", mb: 1, alignItems: "flex-start" }}>
                                <Typography variant="body2" sx={{ color: "rgba(16, 29, 59, 0.6)", width: "90px", textAlign: "left" }}>
                                  Bid Amount:
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#101D3B", fontWeight: 700, textAlign: "left" }}>
                                  ${bid.amount?.toFixed(2) || "0.00"}
                                </Typography>
                              </Box>
                              
                              <Box sx={{ display: "flex", mb: 1, alignItems: "flex-start" }}>
                                <Typography variant="body2" sx={{ color: "rgba(16, 29, 59, 0.6)", width: "90px", textAlign: "left" }}>
                                  Won on:
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#101D3B", fontWeight: 500, textAlign: "left" }}>
                                  {formatDate(bid.updatedAt)}
                                </Typography>
                              </Box>
                              
                              {bid.clientInfo?.contactEmail && (
                                <Box sx={{ display: "flex", mb: 1, alignItems: "flex-start" }}>
                                  <Typography variant="body2" sx={{ color: "rgba(16, 29, 59, 0.6)", width: "90px", textAlign: "left" }}>
                                    Client Email:
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: "#101D3B", fontWeight: 500, textAlign: "left" }}>
                                    {bid.clientInfo.contactEmail}
                                  </Typography>
                                </Box>
                              )}
                            </Grid>
                          </Grid>
                        </Grid>
                        
                        <Grid item xs={12} sm={4} sx={{ 
                          display: "flex", 
                          flexDirection: { xs: "row", sm: "column" },
                          alignItems: { xs: "flex-start", sm: "flex-end" },
                          justifyContent: { xs: "flex-start", sm: "center" }, 
                          gap: 1.5,
                          height: "100%"
                        }}>
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<VisibilityIcon />}
                            onClick={() => goToServiceRequest(bid.requestId)}
                            sx={{ 
                              mt: { xs: 1, sm: 0 },
                              borderRadius: "5px",
                              borderColor: "rgba(16, 29, 59, 0.2)",
                              color: "#101D3B",
                              "&:hover": {
                                borderColor: "#101D3B",
                                filter: "brightness(0.9)"
                              }
                            }}
                          >
                            View Details
                          </Button>
                          {bid.invoiceSent ? (
                            <Chip 
                              sx={{ 
                                mt: { xs: 1, sm: 1.5 },
                                bgcolor: "rgba(0, 171, 155, 0.12)",
                                color: "#00AB9B",
                                fontWeight: 600,
                                border: "1px solid rgba(0, 171, 155, 0.3)",
                                borderRadius: "5px",
                                padding: "8px 4px",
                                height: "36px",
                                "& .MuiChip-icon": {
                                  color: "#00AB9B"
                                },
                                "& .MuiChip-label": {
                                  padding: "0 12px",
                                  fontSize: "0.875rem"
                                }
                              }}
                              icon={<CheckCircleIcon />} 
                              label="Invoice Sent"
                            />
                          ) : (
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<SendIcon />}
                              onClick={() => handleSendInvoice(bid)}
                              disabled={invoiceCreating || !bid.clientInfo?.contactEmail}
                              sx={{ 
                                mt: { xs: 1, sm: 1.5 },
                                borderRadius: "5px",
                                bgcolor: "#00AB9B",
                                "&:hover": {
                                  bgcolor: "#00AB9B",
                                  filter: "brightness(0.9)"
                                },
                                transition: "background-color 0.3s ease"
                              }}
                            >
                              Send Invoice
                            </Button>
                          )}
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ py: 3, textAlign: "center" }}>
                <Typography variant="body1" color="text.secondary">
                  You don't have any winning bids yet. When clients accept your bids, they will appear here.
                </Typography>
              </Box>
            )}
            
            {invoiceCreated && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="success">
                  Invoice created and sent successfully!
                </Alert>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CompletedStep; 